import { Component, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { BehaviorSubject, filter, firstValueFrom } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { OptionWindow } from 'src/app/enum/option-window-enums';
import { ProfileUserEnums } from 'src/app/enum/profile-user-enums';
import { WindowEnums } from 'src/app/enum/window-enums';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { StoreModel } from 'src/app/models/archive/store.model';
import { OptionWindowModel } from 'src/app/models/authentication/option-window.model';
import { UserModel } from 'src/app/models/authentication/user.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { StorePriceModel } from 'src/app/models/subrental/store-price.model';
import { StorePriceTableModel } from 'src/app/models/tables/subrental/store-price-table.model';
import { StoreService } from 'src/app/services/archive/store.service';
import { OptionWindowService } from 'src/app/services/authentication/option-window.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { StorePriceService } from 'src/app/services/subrental/store-price.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import Swal from 'sweetalert2';
import { ImportStorePriceComponent } from '../import-store-price/import-store-price.component';
import { RegisterStorePriceComponent } from '../register-store-price/register-store-price.component';

@Component({
  selector: 'app-store-price',
  templateUrl: './store-price.component.html',
  styleUrls: ['./store-price.component.scss'],
  standalone: false
})
export class StorePriceComponent implements OnInit {

  private readonly branchService = inject(BranchService);
  private readonly storeService = inject(StoreService);
  private readonly storePriceService = inject(StorePriceService);
  private readonly headerService = inject(HeaderService);
  private readonly optionWindowService = inject(OptionWindowService);
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly userAuthenticatedService = inject(UserAuthenticatedService);
  private readonly windowService = inject(WindowService);

  @ViewChild('storePriceImportModal') storePriceImportModalComponent?: CustomModalComponent | null = null;
  @ViewChild('storePriceRegisterModal') storePriceRegisterComponent?: CustomModalComponent | null = null;

  ascendant: boolean = true;
  columnSort: string | null = null;
  filters: ColumnResponseModel[] = [];
  inputsForRegisterModals$ = new BehaviorSubject<any>({
    storePriceModel: new StorePriceModel(),
    openModal: false,
    openFrom: ''
  });
  isOpened: string | undefined = undefined;
  limit: number = 10;
  nameWindow: string = 'Precios por Bodega';
  openFromRegister: string = "";
  page: number = 1;
  permissionUpdatable: boolean = false;
  permissionsUser: Array<OptionWindowModel> = new Array<OptionWindowModel>();
  projectName = ProjectName.projectName;
  storeCode: number | null = null;
  storeData: PagedResponseModel<StorePriceTableModel> = new PagedResponseModel<StorePriceTableModel>();
  storeHeader: ColumnResponseModel[] = [];
  storePriceRegister: StorePriceModel = new StorePriceModel();
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    }, {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      action: 'delete'
    },
  ];
  url: string = '';
  userAuthenticated: UserModel = new UserModel();
  componentStorePriceImportModal = ImportStorePriceComponent;
  componentRegisterModal = RegisterStorePriceComponent;

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem('store-price');
    if (filters) this.filters = JSON.parse(filters);
    this.getPermission();
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getAllStorePricesWithPagination(),
    ]);
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async createStorePrice(): Promise<void> {
    this.openFromRegister = "createStorePrice";
    this.openRegisterModal();
  }

  async deleteStorePrice(storePrice: StorePriceTableModel) {
    if (this.permissionUpdatable) {
      const result = await Swal.fire({
        title: 'Atención',
        text: "¿Realmente desea eliminar el precio por bodega?",
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Si',
        cancelButtonText: 'No'
      });
      if (result.isConfirmed) {
        try {
          await firstValueFrom(this.storePriceService.deleteStorePrice(storePrice.storePriceId));
          this.toastHandlerService.showToast('success', `El registro se eliminó exitosamente.`, '');
          this.getAllStorePricesWithPagination();
        } catch (error) {
          console.error('Error al eliminar el precio por bodega.', error);
        }
      }
    } else {
      this.toastHandlerService.showToast('error', `El usuario no tiene permisos para eliminar el precio por bodega.
        Comuníquese con el administrador del sistema.`, 'Error');
    }
  }

  async editStorePrice(id: number) {
    try {
      this.storePriceRegister = await firstValueFrom(this.storePriceService.getStorePriceById(id));
      this.openFromRegister = "editStorePrice";
      this.openRegisterModal();
    } catch (error) {
      console.error('error al obtener el precio por bodega por id.', error);
    }
  }

  async exportGeneralConsultation() {
    try {
      const response = await firstValueFrom(this.storePriceService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant));
      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Precios por Bodega.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al exportar los precios por bodega:', error);
    }
  }

  async getAllStorePricesWithPagination() {
    try {
      this.storeData = await firstValueFrom(this.storePriceService.getAllStorePricesWithPagination(
        this.page, this.limit, this.columnSort, this.ascendant, this.filters
      ));
    } catch (error) {
      console.error('Error al buscar los datos de precios por bodega.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.storeHeader.length === 0) {
        this.storeHeader = await firstValueFrom(this.storePriceService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta de precios por bodega.', error);
    }
  }

  async getPermission() {
    const url = this.router.url as string
    this.url = url.substring(1);
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast('error', `No se permite tener dos ventanas/pestañas de ${this.nameWindow}
          abiertas.`, 'Error');
      }
    } else {
      localStorage.clear();
    }
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
      this.getPermissionsForUserByWindow();
    } catch (error) {
      console.error('Error al obtener los permisos de la ventana.', error);
    }
  }

  async getPermissionsForUserByWindow() {
    try {
      this.userAuthenticated = await firstValueFrom(this.userAuthenticatedService.userObservable.pipe(filter(user => user && user.userId !== 0)));
      this.permissionsUser = await firstValueFrom(this.optionWindowService.getPermissionsForUserByWindow(WindowEnums.StorePrice));
    } catch (error) {
      console.error('Error obteniendo permisos para el usuario por ventana:', error);
    }
  }

  async getStore(branchId: number, storeCode: number): Promise<StoreModel | null> {
    try {
      return await firstValueFrom(this.storeService.getStoreByCode(branchId, storeCode));
    } catch (error) {
      console.error(`Error al obtener el centro de costos por código`, error);
      return null;
    }
  }

  async onAction(event: { action: string, item?: StorePriceTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      await this.editStorePrice(event.item!.id);
    }
    if (event.action === 'delete' && event.item) {
      this.deleteStorePrice(event.item);
    }
  }

  async onExport() {
    if (this.userAuthenticated.profileUser.code == ProfileUserEnums.administrador || this.permissionsUser.find(x => x.code == OptionWindow.Exportar)) {
      await this.exportGeneralConsultation();
    } else {
      this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para exportar precios por bodegas.
      comuniquese con el administrador del sistema.`, 'Atención');
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);

    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onImport() {
    if (this.userAuthenticated.profileUser.code == ProfileUserEnums.administrador || this.permissionsUser.find(x => x.code == OptionWindow.Importar)) {
      if (this.permissionUpdatable) {
        this.storePriceImportModalComponent && this.storePriceImportModalComponent.show();
      } else {
        this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para la edición de precios por bodega.
        Comuníquese con el administrador del sistema.`, 'Atención');
      }
    } else {
      this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para importar precios por bodegas.
      comuniquese con el administrador del sistema.`, 'Atención');
    }
  }

  async onPageChange(event: { page: number; pageSize: number; sortField?: string; sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField ?? null;
    this.ascendant = event.sortOrder === 1;
    await this.getAllStorePricesWithPagination();
  }

  async onTableFilterChange(event: { filters: ColumnResponseModel[] }) {
    this.filters = event.filters || [];
  }

  openRegisterModal() {
    this.inputsForRegisterModals$.next({
      storePriceModel: this.storePriceRegister,
      openFrom: this.openFromRegister
    });
    this.storePriceRegisterComponent && this.storePriceRegisterComponent.show();
    this.storePriceRegister = new StorePriceModel();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  reloadList(reload: boolean) {
    if (reload) {
      this.getAllStorePricesWithPagination();
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

}
